import React, { useState, useEffect } from 'react';
import { ArrowRight, Briefcase, Heart, Play, X, ChevronLeft, ChevronRight, ZoomIn, Instagram } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import GalleryModal from './GalleryModal';
import Lightbox from './Lightbox';

const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: '' });
  const [galleryModal, setGalleryModal] = useState({ isOpen: false, images: [] });
  const [lightbox, setLightbox] = useState({ isOpen: false, images: [], currentIndex: 0 });
  const { t, language } = useTranslation();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/portfolio`);
        const data = await response.json();
        setProjects(data);
      } catch (error) {
        console.error('Error fetching portfolio projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = activeFilter === 'all' 
    ? projects
    : projects.filter(project => project.type === activeFilter);

  const openVideoModal = (videoUrl) => {
    setVideoModal({ isOpen: true, videoUrl });
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setVideoModal({ isOpen: false, videoUrl: '' });
    document.body.style.overflow = 'unset';
  };

  const openGalleryModal = (images) => {
    setGalleryModal({ isOpen: true, images });
    document.body.style.overflow = 'hidden';
  };

  const closeGalleryModal = () => {
    setGalleryModal({ isOpen: false, images: [] });
    document.body.style.overflow = 'unset';
  };

  const openLightbox = (images, startIndex = 0) => {
    setLightbox({ isOpen: true, images, currentIndex: startIndex });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox({ isOpen: false, images: [], currentIndex: 0 });
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? prev.images.length - 1 : prev.currentIndex - 1
    }));
  };

  const getProjectTitle = (project) => {
    if (language === 'de' && project.title_de) return project.title_de;
    if (language === 'en' && project.title_en) return project.title_en;
    return project.title || project.title_en || project.title_de;
  };

  const getProjectDescription = (project) => {
    if (language === 'de' && project.description_de) return project.description_de;
    if (language === 'en' && project.description_en) return project.description_en;
    return project.description || project.description_en || project.description_de;
  };

  const getProjectServices = (project) => {
    if (language === 'de' && project.services_de) return project.services_de;
    if (language === 'en' && project.services_en) return project.services_en;
    return null;
  };

  return (
    <section id="portfolio" className="section-spacing-large bg-white">
      <div className="container-cift">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="gold-accent mx-auto" />
          <h2 className="section-title mb-6">
            {t('portfolio.headline')}
          </h2>
          <p className="subtitle">
            {t('portfolio.body')}
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-6 py-2 font-semibold text-sm uppercase tracking-wider transition-all ${
              activeFilter === 'all'
                ? 'text-brand-gold'
                : 'border border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
            style={activeFilter === 'all' ? { 
              border: '1.5px solid #b8960c', 
              background: 'rgba(184, 150, 12, 0.06)' 
            } : {}}
          >
            {t('portfolio.filter.all')}
          </button>
          <button
            onClick={() => setActiveFilter('business')}
            className={`px-6 py-2 font-semibold text-sm uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeFilter === 'business'
                ? 'text-brand-gold'
                : 'border border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
            style={activeFilter === 'business' ? { 
              border: '1.5px solid #b8960c', 
              background: 'rgba(184, 150, 12, 0.06)' 
            } : {}}
          >
            <Briefcase size={16} />
            {t('portfolio.filter.business')}
          </button>
          <button
            onClick={() => setActiveFilter('wedding')}
            className={`px-6 py-2 font-semibold text-sm uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeFilter === 'wedding'
                ? 'text-brand-gold'
                : 'border border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
            style={activeFilter === 'wedding' ? { 
              border: '1.5px solid #b8960c', 
              background: 'rgba(184, 150, 12, 0.06)' 
            } : {}}
          >
            <Heart size={16} />
            {t('portfolio.filter.weddings')}
          </button>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">{t('portfolio.loading') || 'Lädt Portfolio...'}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">{t('portfolio.noProjects') || 'Keine Projekte gefunden.'}</p>
            </div>
          ) : (
            filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="group bg-white border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                {/* Thumbnail with Overlay */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                  <img 
                    src={project.image} 
                    alt={getProjectTitle(project)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Category Badge - top left */}
                  <div className="absolute top-4 left-4 z-10">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase backdrop-blur-md ${
                      project.type === 'business' 
                        ? 'bg-black/70 text-white' 
                        : 'bg-gold/70 text-white'
                    }`}>
                      {project.type === 'business' ? (
                        <>
                          <Briefcase size={12} />
                          BUSINESS
                        </>
                      ) : (
                        <>
                          <Heart size={12} />
                          {t('nav.weddings')}
                        </>
                      )}
                    </span>
                  </div>

                  {/* Status Badge - top right (for ongoing projects) */}
                  {(project.status_badge_de || project.status_badge_en) && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold uppercase backdrop-blur-md bg-brand-gold/90 text-white">
                        {language === 'de' ? project.status_badge_de : project.status_badge_en}
                      </span>
                    </div>
                  )}

                  {/* Video Play Overlay */}
                  {project.video_url && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={28} className="text-brand-dark ml-1" />
                      </div>
                    </div>
                  )}

                  {/* Gallery Overlay (for projects with gallery but no video) */}
                  {project.gallery && !project.video_url && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <ZoomIn size={28} className="text-brand-dark" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Year */}
                  {project.year && (
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                      {t('portfolio.year')} {project.year}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-3 leading-tight">
                    {getProjectTitle(project)}
                  </h3>

                  {/* Client or Location */}
                  {(project.client || project.location) && (
                    <p className="text-sm text-gray-600 mb-3">
                      {project.client || project.location}
                    </p>
                  )}

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {getProjectDescription(project)}
                  </p>

                  {/* Services Tags */}
                  {getProjectServices(project) && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {getProjectServices(project).split('·').map((service, idx) => (
                          <span 
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded"
                          >
                            {service.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Video Button */}
                  {project.video_url && (
                    <button
                      onClick={() => openVideoModal(project.video_url)}
                      className="inline-flex items-center gap-2 text-brand-gold hover:text-brand-dark font-semibold text-sm transition-colors group/btn"
                    >
                      {t('portfolio.watchFilm')}
                      <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  )}

                  {/* Gallery Button (for projects with gallery) */}
                  {project.gallery && (
                    <button
                      onClick={() => openGalleryModal(project.gallery)}
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white font-semibold text-sm uppercase tracking-wider transition-all"
                    >
                      {t('portfolio.viewGallery')}
                    </button>
                  )}

                  {/* Instagram Button (for projects with Instagram link) */}
                  {project.instagram_url && (
                    <a
                      href={project.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white font-semibold text-sm uppercase tracking-wider transition-all"
                    >
                      <Instagram size={18} />
                      {t('portfolio.viewInstagram') || (language === 'de' ? 'Zum Instagram-Kanal' : 'View Instagram Channel')}
                      <ArrowRight size={16} />
                    </a>
                  )}

                  {/* CTA Button (for projects with custom call-to-action) */}
                  {project.cta_button_url && (
                    <button
                      onClick={() => {
                        const element = document.querySelector(project.cta_button_url);
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="inline-flex items-center gap-2 mt-3 px-6 py-3 bg-brand-dark text-white hover:bg-[#2a2f3e] font-semibold text-sm uppercase tracking-wider transition-all"
                    >
                      {language === 'de' ? project.cta_button_text_de : project.cta_button_text_en}
                      <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            {t('portfolio.body')}
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('kontakt');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary inline-flex items-center gap-2 group"
          >
            {t('portfolio.cta')}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeVideoModal}
        >
          <div className="relative w-full max-w-6xl">
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute -top-12 right-0 text-white hover:text-gold transition-colors"
              aria-label="Close video"
            >
              <X size={32} />
            </button>

            {/* Video iframe */}
            <div 
              className="relative aspect-video bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={videoModal.videoUrl}
                title="Project Video"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={galleryModal.isOpen}
        images={galleryModal.images}
        onClose={closeGalleryModal}
        onImageClick={(index) => {
          openLightbox(galleryModal.images, index);
        }}
      />

      {/* Lightbox */}
      <Lightbox
        isOpen={lightbox.isOpen}
        images={lightbox.images}
        currentIndex={lightbox.currentIndex}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </section>
  );
};

export default PortfolioSection;
